// @ts-ignore - vendored self-contained Three.js module.
import * as THREE from '../../vendor/three.module.min.js';
import { PICKUP_INFO } from '../game/pickup-info.js';
import type { EnemyKind, HazardKind, LevelConfig, ObstacleKind, PickupKind } from '../game/types.js';
import { MaterialLibrary, type TankPalette } from './MaterialLibrary.js';

export interface TankModel {
  root: any;
  turret: any;
  muzzle: any;
  shield: any;
  hitParts: any[];
  leftTrack: any;
  rightTrack: any;
  antennaTip: any;
  drone: any;
}

export interface ObstacleModel {
  root: any;
  damageMeshes: any[];
}

export class ModelFactory {
  private readonly shared = {
    box: new THREE.BoxGeometry(1, 1, 1),
    cylinder12: new THREE.CylinderGeometry(1, 1, 1, 12),
    cylinder16: new THREE.CylinderGeometry(1, 1, 1, 16),
    sphere12: new THREE.SphereGeometry(1, 12, 8),
    circle24: new THREE.CircleGeometry(1, 24),
    tetra: new THREE.TetrahedronGeometry(1, 0),
  };
  private readonly projectileGeometry = {
    normalCore: new THREE.SphereGeometry(0.19, 12, 8),
    strongCore: new THREE.SphereGeometry(0.25, 12, 8),
    normalRing: new THREE.TorusGeometry(0.23, 0.035, 6, 12),
    strongRing: new THREE.TorusGeometry(0.29, 0.035, 6, 12),
    normalTrail: new THREE.ConeGeometry(0.12, 0.5, 8),
    strongTrail: new THREE.ConeGeometry(0.17, 0.72, 8),
  };
  /**
   * Shared geometry cache keyed by a shape-and-parameters signature.
   *
   * Tank, pickup, and mine parts previously allocated geometry per instance,
   * roughly 19 geometries per tank. Removing only the root without disposing it
   * made renderer.info.memory.geometries grow from 234 to 1,430 in a 50-second
   * run while frame time degraded from 10.4ms to 48.6ms.
   *
   * Cached geometry is persistent and must never be mutated with rotate, translate,
   * or center after creation. All call sites apply transforms to meshes instead.
   */
  private readonly geometryCache = new Map<string, any>();
  /** Persistent shared materials for shields, pickup shells, and similar parts. */
  private readonly materialCache = new Map<string, any>();
  /** Cache hit counters exposed to diagnostics. */
  readonly extrudeCacheStats = { hits: 0, misses: 0 };
  /**
   * SpriteMaterial cache keyed by text and color. Pickup creation previously
   * rerasterized two canvases and uploaded two textures every time. Content has
   * only six variants, so the scene now reuses twelve persistent textures while
   * each placement still receives its own Sprite Object3D.
   */
  private readonly spriteMaterialCache = new Map<string, any>();

  constructor(private readonly materials: MaterialLibrary) {
    for (const geometry of [...Object.values(this.shared), ...Object.values(this.projectileGeometry)]) {
      geometry.userData.persistent = true;
    }
    // Expose cache counters for diagnostics without affecting game logic.
    if (typeof window !== 'undefined') {
      (window as any).__toonTankCache ??= {};
      (window as any).__toonTankCache.tankExtrude = this.extrudeCacheStats;
    }
  }

  /** Get shared geometry or build a persistent entry that clearWorld will preserve. */
  private cachedGeometry(key: string, build: () => any): any {
    const cached = this.geometryCache.get(key);
    if (cached) {
      this.extrudeCacheStats.hits += 1;
      return cached;
    }
    this.extrudeCacheStats.misses += 1;
    const geometry = build();
    geometry.userData.persistent = true;
    this.geometryCache.set(key, geometry);
    return geometry;
  }

  /** Semantic alias for cached hull and turret ExtrudeGeometry. */
  private cachedExtrude(key: string, build: () => any): any {
    return this.cachedGeometry(key, build);
  }

  /**
   * Reuse MeshPhysicalMaterial instances for shields and pickup shells.
   * Shared materials stay persistent because disposing one entity must not
   * invalidate every live entity using the same material.
   */
  private cachedMaterial(key: string, build: () => any): any {
    const cached = this.materialCache.get(key);
    if (cached) return cached;
    const material = build();
    material.userData ??= {};
    material.userData.persistent = true;
    this.materialCache.set(key, material);
    return material;
  }

  private box(width: number, height: number, depth: number, ws = 1, hs = 1, ds = 1): any {
    return this.cachedGeometry(`box:${width}:${height}:${depth}:${ws}:${hs}:${ds}`, () => new THREE.BoxGeometry(width, height, depth, ws, hs, ds));
  }

  private cylinder(top: number, bottom: number, height: number, segments: number): any {
    return this.cachedGeometry(`cyl:${top}:${bottom}:${height}:${segments}`, () => new THREE.CylinderGeometry(top, bottom, height, segments));
  }

  private cone(radius: number, height: number, segments: number): any {
    return this.cachedGeometry(`cone:${radius}:${height}:${segments}`, () => new THREE.ConeGeometry(radius, height, segments));
  }

  private sphere(radius: number, ws: number, hs: number): any {
    return this.cachedGeometry(`sph:${radius}:${ws}:${hs}`, () => new THREE.SphereGeometry(radius, ws, hs));
  }

  private torus(radius: number, tube: number, rs: number, ts: number, arc?: number): any {
    return this.cachedGeometry(`tor:${radius}:${tube}:${rs}:${ts}:${arc ?? 'full'}`, () => (arc === undefined
      ? new THREE.TorusGeometry(radius, tube, rs, ts)
      : new THREE.TorusGeometry(radius, tube, rs, ts, arc)));
  }

  private circle(radius: number, segments: number): any {
    return this.cachedGeometry(`cir:${radius}:${segments}`, () => new THREE.CircleGeometry(radius, segments));
  }

  createTank(palette: TankPalette, kind: EnemyKind | 'player' = 'player'): TankModel {
    const mats = this.materials.tank(palette);
    const root = new THREE.Group();
    root.name = `tank-${kind}`;

    const shadow = new THREE.Mesh(this.shared.circle24, this.materials.shadow);
    shadow.rotation.x = -Math.PI / 2;
    shadow.scale.set(kind === 'boss' ? 2.45 : 1.55, kind === 'boss' ? 2.45 : 1.55, 1);
    shadow.position.y = 0.035;
    root.add(shadow);

    const tankScale = kind === 'boss' ? 1.55 : kind === 'bruiser' ? 1.13 : kind === 'sniper' ? 1.02 : 1;
    const chassis = new THREE.Group();
    chassis.scale.setScalar(tankScale);
    chassis.position.y = 0.08;
    root.add(chassis);

    // Hull shape is independent of kind and palette; kind differences use chassis scale and barrel dimensions.
    const hullGeometry = this.cachedExtrude('hull:0.48:0.09:0.08:1', () => {
      const hullShape = new THREE.Shape();
      hullShape.moveTo(-0.95, -1.15);
      hullShape.lineTo(-1.12, -0.72);
      hullShape.lineTo(-1.02, 0.78);
      hullShape.lineTo(-0.67, 1.18);
      hullShape.lineTo(0.67, 1.18);
      hullShape.lineTo(1.02, 0.78);
      hullShape.lineTo(1.12, -0.72);
      hullShape.lineTo(0.95, -1.15);
      hullShape.closePath();
      const geometry = new THREE.ExtrudeGeometry(hullShape, { depth: 0.48, bevelEnabled: true, bevelSize: 0.09, bevelThickness: 0.08, bevelSegments: 1 });
      geometry.rotateX(-Math.PI / 2);
      geometry.center();
      return geometry;
    });
    const hull = this.meshWithOutline(hullGeometry, mats.primary, 1.035);
    hull.position.y = 0.58;
    hull.castShadow = true;
    hull.receiveShadow = true;
    chassis.add(hull);

    const lowerHull = new THREE.Mesh(this.shared.box, mats.dark);
    lowerHull.scale.set(1.78, 0.35, 1.9);
    lowerHull.position.y = 0.34;
    lowerHull.castShadow = true;
    chassis.add(lowerHull);

    const trackGeometry = this.box(0.39, 0.48, 2.55, 1, 1, 3);
    const leftTrack = this.meshWithOutline(trackGeometry, this.materials.darkRubber, 1.045);
    leftTrack.position.set(-1.1, 0.39, 0);
    chassis.add(leftTrack);
    const rightTrack = leftTrack.clone();
    rightTrack.position.x = 1.1;
    chassis.add(rightTrack);

    const treadMaterial = mats.trim;
    const wheels = new THREE.InstancedMesh(this.shared.cylinder16, treadMaterial, 10);
    const hubs = new THREE.InstancedMesh(this.shared.cylinder12, mats.dark, 10);
    const treads = new THREE.InstancedMesh(this.shared.box, this.materials.darkMetal, 14);
    const detailDummy = new THREE.Object3D();
    let wheelIndex = 0;
    let treadIndex = 0;
    for (const side of [-1, 1]) {
      for (let i = -2; i <= 2; i += 1) {
        detailDummy.position.set(side * 1.32, 0.4, i * 0.46);
        detailDummy.rotation.set(0, 0, Math.PI / 2);
        detailDummy.scale.set(0.34, 0.16, 0.34);
        detailDummy.updateMatrix();
        wheels.setMatrixAt(wheelIndex, detailDummy.matrix);
        detailDummy.position.x += side * 0.03;
        detailDummy.scale.set(0.19, 0.18, 0.19);
        detailDummy.updateMatrix();
        hubs.setMatrixAt(wheelIndex, detailDummy.matrix);
        wheelIndex += 1;
      }
      for (let i = -3; i <= 3; i += 1) {
        detailDummy.position.set(side * 1.31, 0.69, i * 0.39);
        detailDummy.rotation.set(0, 0, 0);
        detailDummy.scale.set(0.46, 0.08, 0.27);
        detailDummy.updateMatrix();
        treads.setMatrixAt(treadIndex++, detailDummy.matrix);
      }
    }
    for (const mesh of [wheels, hubs, treads]) {
      mesh.instanceMatrix.needsUpdate = true;
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      chassis.add(mesh);
    }

    const frontBumper = this.meshWithOutline(this.box(1.56, 0.3, 0.34), mats.trim, 1.04);
    frontBumper.position.set(0, 0.56, -1.24);
    chassis.add(frontBumper);
    const bumperTeeth = [-0.56, 0, 0.56];
    for (const x of bumperTeeth) {
      const tooth = new THREE.Mesh(this.cone(0.13, 0.38, 4), mats.secondary);
      tooth.rotation.x = -Math.PI / 2;
      tooth.rotation.z = Math.PI / 4;
      tooth.position.set(x, 0.58, -1.48);
      chassis.add(tooth);
    }

    const rearPack = this.meshWithOutline(this.box(1.3, 0.56, 0.48), mats.secondary, 1.04);
    rearPack.position.set(0, 0.73, 1.08);
    chassis.add(rearPack);
    for (const x of [-0.42, 0.42]) {
      const exhaust = new THREE.Mesh(this.shared.cylinder12, this.materials.darkMetal);
      exhaust.rotation.x = Math.PI / 2;
      exhaust.scale.set(0.16, 0.42, 0.16);
      exhaust.position.set(x, 0.92, 1.35);
      chassis.add(exhaust);
      const glow = new THREE.Mesh(this.circle(0.12, 12), mats.signal);
      glow.position.set(x, 0.92, 1.78);
      glow.rotation.x = Math.PI / 2;
      chassis.add(glow);
    }

    const turret = new THREE.Group();
    turret.position.y = 1.02;
    chassis.add(turret);
    const turretRing = this.meshWithOutline(this.cylinder(0.76, 0.86, 0.28, 12), mats.trim, 1.045);
    turretRing.position.y = 0.04;
    turret.add(turretRing);

    // Turret shape is also independent of kind and palette.
    const turretGeometry = this.cachedExtrude('turret:0.43:0.07:0.05:1', () => {
      const turretShape = new THREE.Shape();
      turretShape.moveTo(-0.67, -0.65);
      turretShape.lineTo(-0.82, 0.28);
      turretShape.lineTo(-0.42, 0.72);
      turretShape.lineTo(0.42, 0.72);
      turretShape.lineTo(0.82, 0.28);
      turretShape.lineTo(0.67, -0.65);
      turretShape.closePath();
      const geometry = new THREE.ExtrudeGeometry(turretShape, { depth: 0.43, bevelEnabled: true, bevelSize: 0.07, bevelThickness: 0.05, bevelSegments: 1 });
      geometry.rotateX(-Math.PI / 2);
      geometry.center();
      return geometry;
    });
    const turretBody = this.meshWithOutline(turretGeometry, mats.secondary, 1.04);
    turretBody.position.y = 0.42;
    turret.add(turretBody);

    const hatch = this.meshWithOutline(this.cylinder(0.35, 0.38, 0.2, 12), mats.primary, 1.04);
    hatch.position.set(0.18, 0.76, 0.13);
    turret.add(hatch);
    const hatchHandle = new THREE.Mesh(this.torus(0.16, 0.035, 6, 12, Math.PI), mats.trim);
    hatchHandle.rotation.x = Math.PI / 2;
    hatchHandle.position.set(0.18, 0.93, 0.11);
    turret.add(hatchHandle);

    const barrelLength = kind === 'sniper' ? 2.35 : kind === 'boss' ? 2.1 : 1.72;
    const barrelRadius = kind === 'bruiser' || kind === 'boss' ? 0.18 : 0.135;
    const barrel = this.meshWithOutline(this.cylinder(barrelRadius * 0.78, barrelRadius, barrelLength, 12), mats.trim, 1.05);
    barrel.rotation.x = Math.PI / 2;
    barrel.position.set(0, 0.49, -0.72 - barrelLength * 0.5);
    turret.add(barrel);

    const barrelBand = new THREE.Mesh(this.cylinder(barrelRadius * 1.42, barrelRadius * 1.42, 0.25, 12), mats.primary);
    barrelBand.rotation.x = Math.PI / 2;
    barrelBand.position.set(0, 0.49, -1.1);
    turret.add(barrelBand);
    const muzzle = new THREE.Object3D();
    muzzle.position.set(0, 0.49, -0.72 - barrelLength);
    turret.add(muzzle);
    const muzzleBrake = this.meshWithOutline(this.cylinder(barrelRadius * 1.55, barrelRadius * 1.35, 0.34, 12), mats.dark, 1.05);
    muzzleBrake.rotation.x = Math.PI / 2;
    muzzleBrake.position.copy(muzzle.position);
    muzzleBrake.position.z += 0.11;
    turret.add(muzzleBrake);

    const sight = new THREE.Group();
    sight.position.set(-0.48, 0.64, -0.05);
    turret.add(sight);
    const sightBody = new THREE.Mesh(this.shared.box, mats.dark);
    sightBody.scale.set(0.22, 0.22, 0.42);
    sight.add(sightBody);
    const lens = new THREE.Mesh(this.circle(0.1, 12), mats.signal);
    lens.rotation.x = Math.PI / 2;
    lens.position.z = -0.22;
    sight.add(lens);

    const antenna = new THREE.Mesh(this.cylinder(0.025, 0.035, 0.95, 6), mats.dark);
    antenna.position.set(0.54, 1.08, 0.4);
    antenna.rotation.z = -0.12;
    turret.add(antenna);
    const antennaTip = new THREE.Mesh(this.shared.sphere12, mats.signal);
    antennaTip.scale.setScalar(0.09);
    antennaTip.position.set(0.65, 1.55, 0.4);
    turret.add(antennaTip);

    // The BackSide outline material prevents a scaled opaque FrontSide shell from hiding the badge.
    const frontBadge = this.createStarBadge(mats.signal, this.materials.outline);
    frontBadge.scale.setScalar(0.22);
    frontBadge.rotation.x = -Math.PI / 2;
    frontBadge.position.set(0, 0.87, -1.17);
    chassis.add(frontBadge);

    if (kind === 'scout') this.decorateScout(turret, mats);
    if (kind === 'bruiser') this.decorateBruiser(chassis, turret, mats);
    if (kind === 'sniper') this.decorateSniper(turret, mats);
    if (kind === 'boss') this.decorateBoss(chassis, turret, mats);

    // Cache shield geometry by kind and material by color instead of allocating both per tank.
    const shield = new THREE.Mesh(
      this.sphere(kind === 'boss' ? 2.1 : 1.52, 24, 16),
      this.cachedMaterial(`shield:${palette.signal.toString(16)}`, () => new THREE.MeshPhysicalMaterial({
        color: palette.signal,
        emissive: palette.signal,
        emissiveIntensity: 0.5,
        transparent: true,
        opacity: 0.17,
        roughness: 0.1,
        metalness: 0.05,
        side: THREE.DoubleSide,
        depthWrite: false,
      })),
    );
    shield.scale.y = 0.75;
    shield.position.y = 0.92;
    shield.visible = false;
    root.add(shield);

    const drone = new THREE.Group();
    drone.name = `support-drone-${kind}`;
    drone.visible = false;
    drone.position.set(0, 1.95, 0);
    const droneBody = new THREE.Mesh(this.cachedGeometry('octa:0.28:0', () => new THREE.OctahedronGeometry(0.28, 0)), mats.signal);
    drone.add(droneBody);
    const droneWing = new THREE.InstancedMesh(this.shared.box, mats.trim, 4);
    const droneDummy = new THREE.Object3D();
    for (let i = 0; i < 4; i += 1) {
      const angle = i * Math.PI / 2;
      droneDummy.position.set(Math.cos(angle) * 0.42, 0, Math.sin(angle) * 0.42);
      droneDummy.rotation.set(0, angle, 0);
      droneDummy.scale.set(0.36, 0.05, 0.12);
      droneDummy.updateMatrix();
      droneWing.setMatrixAt(i, droneDummy.matrix);
    }
    droneWing.instanceMatrix.needsUpdate = true;
    drone.add(droneWing);
    root.add(drone);

    root.traverse((object: any) => {
      if (object.isMesh) {
        object.castShadow = false;
        object.receiveShadow = false;
      }
    });
    for (const candidate of [hull, lowerHull, turretBody, barrel]) {
      const mesh = candidate.userData?.primaryMesh ?? candidate;
      if (mesh?.isMesh) {
        mesh.castShadow = true;
        mesh.receiveShadow = true;
      }
    }

    // Merge static meshes after shadow flags are finalized so merged buckets inherit those flags.
    // Tracks, the independently rotating turret, and the animated antenna tip stay separate.
    this.collapseStatic(chassis, `chassis:${kind}`, new Set<any>([turret, leftTrack, rightTrack]));
    this.collapseStatic(turret, `turret:${kind}`, new Set<any>([antennaTip]));

    return {
      root,
      turret,
      muzzle,
      shield,
      hitParts: [hull, turretBody, turretRing],
      leftTrack,
      rightTrack,
      antennaTip,
      drone,
    };
  }

  createCore(accent: number): { root: any; crystal: any; rings: any[]; shield: any } {
    const root = new THREE.Group();
    root.name = 'rainbow-core';
    const base = this.meshWithOutline(new THREE.CylinderGeometry(1.45, 1.7, 0.65, 10), this.materials.darkMetal, 1.035);
    base.position.y = 0.34;
    base.castShadow = true;
    root.add(base);

    const trim = new THREE.Mesh(new THREE.TorusGeometry(1.22, 0.12, 8, 24), this.materials.emissive(accent, 0.75));
    trim.rotation.x = Math.PI / 2;
    trim.position.y = 0.71;
    root.add(trim);

    // Any nonzero transmission triggers a full renderTransmissionPass. A measured 0.08
    // added 222 draw calls per frame with no perceptible benefit, so emissive and clearcoat
    // provide the crystal finish without the extra scene pass.
    const crystalMaterial = new THREE.MeshPhysicalMaterial({
      color: 0xfff06b,
      emissive: 0xffb82e,
      emissiveIntensity: 1.25,
      roughness: 0.16,
      clearcoat: 1,
    });
    const crystal = this.createStarBadge(crystalMaterial, this.materials.outline);
    crystal.scale.setScalar(0.95);
    crystal.rotation.x = -Math.PI / 2;
    crystal.position.y = 1.68;
    root.add(crystal);

    const rings: any[] = [];
    for (let i = 0; i < 2; i += 1) {
      const ring = new THREE.Mesh(new THREE.TorusGeometry(1.18 + i * 0.24, 0.035, 6, 32), this.materials.emissive(i === 0 ? accent : 0xff7fc4, 1));
      ring.position.y = 1.65;
      ring.rotation.x = i === 0 ? Math.PI / 2 : 0.5;
      root.add(ring);
      rings.push(ring);
    }

    const shield = new THREE.Mesh(
      new THREE.SphereGeometry(2.05, 24, 16),
      new THREE.MeshPhysicalMaterial({ color: accent, emissive: accent, emissiveIntensity: 0.25, transparent: true, opacity: 0.11, side: THREE.DoubleSide, depthWrite: false }),
    );
    shield.position.y = 1.1;
    shield.scale.y = 0.72;
    root.add(shield);
    return { root, crystal, rings, shield };
  }

  createObstacle(kind: ObstacleKind, width: number, depth: number, level: LevelConfig): ObstacleModel {
    const root = new THREE.Group();
    const damageMeshes: any[] = [];
    if (kind === 'metal') {
      const body = this.meshWithOutline(new THREE.BoxGeometry(width, 1.2, depth), this.materials.toon(level.scenery === 'moon' ? 0x59637f : 0x617a83), 1.025);
      body.position.y = 0.6;
      root.add(body);
      const railMaterial = this.materials.toon(level.palette.rim);
      const posts = new THREE.InstancedMesh(this.shared.box, railMaterial, 2);
      const dummy = new THREE.Object3D();
      [-width * 0.43, width * 0.43].forEach((x, index) => {
        dummy.position.set(x, 0.67, 0);
        dummy.rotation.set(0, 0, 0);
        dummy.scale.set(0.12, 1.45, depth + 0.12);
        dummy.updateMatrix();
        posts.setMatrixAt(index, dummy.matrix);
      });
      posts.instanceMatrix.needsUpdate = true;
      root.add(posts);
      const stripeTexture = this.materials.createHazardTexture(level.scenery === 'moon' ? '#52e8e2' : '#ffd354', '#24344a');
      const stripeMaterial = new THREE.MeshStandardMaterial({ map: stripeTexture, roughness: 0.72 });
      const stripe = new THREE.Mesh(new THREE.BoxGeometry(width * 0.76, 0.27, depth + 0.035), stripeMaterial);
      stripe.position.set(0, 0.94, 0);
      root.add(stripe);
      const rivets = new THREE.InstancedMesh(this.shared.sphere12, this.materials.darkMetal, 6);
      let rivetIndex = 0;
      for (const side of [-1, 1]) {
        for (let i = -1; i <= 1; i += 1) {
          dummy.position.set(side * width * 0.45, 0.85, i * depth * 0.28);
          dummy.rotation.set(0, 0, 0);
          dummy.scale.setScalar(0.09);
          dummy.updateMatrix();
          rivets.setMatrixAt(rivetIndex++, dummy.matrix);
        }
      }
      rivets.instanceMatrix.needsUpdate = true;
      root.add(rivets);
    } else {
      const bodyMat = this.materials.toon(level.scenery === 'moon' ? 0x925b9d : level.scenery === 'desert' ? 0xd56b45 : 0xc88546);
      const body = this.meshWithOutline(new THREE.BoxGeometry(width, 1.25, depth), bodyMat, 1.028);
      body.position.y = 0.63;
      root.add(body);
      damageMeshes.push(body);
      const slatMat = this.materials.toon(level.scenery === 'moon' ? 0xd17ee4 : 0xf5b85f);
      const details = new THREE.InstancedMesh(this.shared.box, slatMat, 4);
      const dummy = new THREE.Object3D();
      let index = 0;
      for (const x of [-0.32, 0.32]) {
        dummy.position.set(width * x, 0.68, 0);
        dummy.rotation.set(0, 0, 0);
        dummy.scale.set(0.13, 1.34, depth + 0.04);
        dummy.updateMatrix();
        details.setMatrixAt(index++, dummy.matrix);
      }
      for (const rotation of [0.34, -0.34]) {
        dummy.position.set(0, 0.77, depth * 0.51);
        dummy.rotation.set(0, 0, rotation);
        dummy.scale.set(width * 0.92, 0.11, 0.15);
        dummy.updateMatrix();
        details.setMatrixAt(index++, dummy.matrix);
      }
      details.instanceMatrix.needsUpdate = true;
      root.add(details);
    }
    root.traverse((object: any) => {
      if (object.isMesh) {
        object.castShadow = false;
        object.receiveShadow = true;
      }
    });
    for (const child of root.children) {
      const mesh = child.userData?.primaryMesh ?? child;
      if (mesh?.isMesh) mesh.castShadow = true;
    }
    return { root, damageMeshes };
  }

  createProjectile(team: 'player' | 'enemy', strong = false): { root: any; core: any; trail: any } {
    const root = new THREE.Group();
    root.name = `projectile-${team}-${strong ? 'strong' : 'normal'}`;
    const material = team === 'player' ? this.materials.projectilePlayer : this.materials.projectileEnemy;
    const trailMaterial = team === 'player' ? this.materials.projectilePlayerTrail : this.materials.projectileEnemyTrail;
    const core = new THREE.Mesh(strong ? this.projectileGeometry.strongCore : this.projectileGeometry.normalCore, material);
    root.add(core);
    const ring = new THREE.Mesh(strong ? this.projectileGeometry.strongRing : this.projectileGeometry.normalRing, material);
    ring.rotation.x = Math.PI / 2;
    root.add(ring);
    const trail = new THREE.Mesh(strong ? this.projectileGeometry.strongTrail : this.projectileGeometry.normalTrail, trailMaterial);
    trail.rotation.x = -Math.PI / 2;
    trail.position.z = strong ? 0.44 : 0.33;
    root.add(trail);
    return { root, core, trail };
  }

  createPickup(kind: PickupKind): { root: any; orbiters: any[]; core: any } {
    const info = PICKUP_INFO[kind];
    const color = info.color;
    const root = new THREE.Group();
    root.userData.pickupKind = kind;
    root.userData.pickupLabel = info.label;
    root.userData.pickupEffect = info.effect;
    root.userData.pickupModel = info.model;
    const shadow = new THREE.Mesh(this.shared.circle24, this.materials.shadow);
    shadow.rotation.x = -Math.PI / 2;
    shadow.scale.set(0.75, 0.75, 1);
    shadow.position.y = 0.03;
    root.add(shadow);

    const core = this.createPickupBody(root, kind, color);

    const orbiters: any[] = [];
    for (let i = 0; i < 3; i += 1) {
      const chip = new THREE.Mesh(this.box(0.18, 0.1, 0.34), this.materials.white);
      chip.position.set(Math.cos(i * Math.PI * 2 / 3) * 0.7, 0.86, Math.sin(i * Math.PI * 2 / 3) * 0.7);
      chip.userData.orbitOffset = i * Math.PI * 2 / 3;
      root.add(chip);
      orbiters.push(chip);
    }

    const icon = this.createTextSprite(info.icon, color);
    icon.position.set(0, 1.55, 0);
    icon.scale.set(0.58, 0.58, 0.58);
    root.add(icon);
    const purpose = this.createLabelSprite(info.label, color);
    purpose.position.set(0, 2.16, 0);
    purpose.scale.set(1.55, 0.58, 1);
    root.add(purpose);
    return { root, orbiters, core };
  }

  private createPickupBody(root: any, kind: PickupKind, color: number): any {
    const body = new THREE.Group();
    // Reuse shell, glow, and cream materials because recurring PvE/PvP pickups would otherwise accumulate them.
    const hex = color.toString(16);
    const shellMaterial = this.cachedMaterial(`pickupShell:${hex}`, () => new THREE.MeshPhysicalMaterial({
      color,
      emissive: color,
      emissiveIntensity: 0.32,
      transparent: true,
      opacity: 0.82,
      roughness: 0.16,
      clearcoat: 1,
    }));
    const glowMaterial = this.cachedMaterial(`pickupGlow:${hex}`, () => this.materials.emissive(color, 1.35));
    const creamMaterial = this.cachedMaterial('pickupCream', () => this.materials.toon(0xfff1b7));
    const darkMaterial = this.materials.darkMetal;
    root.add(body);

    if (kind === 'repair') {
      const pack = this.meshWithOutline(this.box(0.74, 0.62, 0.58), shellMaterial, 1.035);
      pack.position.y = 0.78;
      body.add(pack);
      const cap = new THREE.Mesh(this.cylinder(0.24, 0.28, 0.22, 12), glowMaterial);
      cap.position.y = 1.2;
      body.add(cap);
      const crossVertical = new THREE.Mesh(this.shared.box, this.materials.white);
      crossVertical.scale.set(0.16, 0.08, 0.52);
      crossVertical.position.set(0, 1.13, 0);
      body.add(crossVertical);
      const crossHorizontal = new THREE.Mesh(this.shared.box, this.materials.white);
      crossHorizontal.scale.set(0.52, 0.08, 0.16);
      crossHorizontal.position.set(0, 1.14, 0);
      body.add(crossHorizontal);
      return body;
    }

    if (kind === 'shield') {
      const pedestal = this.meshWithOutline(this.cylinder(0.4, 0.52, 0.34, 12), shellMaterial, 1.035);
      pedestal.position.y = 0.42;
      body.add(pedestal);
      const dome = new THREE.Mesh(
        this.sphere(0.58, 18, 10),
        this.cachedMaterial(`pickupDome:${hex}`, () => new THREE.MeshPhysicalMaterial({
          color,
          emissive: color,
          emissiveIntensity: 0.48,
          transparent: true,
          opacity: 0.38,
          roughness: 0.08,
          metalness: 0.04,
          side: THREE.DoubleSide,
          depthWrite: false,
        })),
      );
      dome.scale.y = 0.62;
      dome.position.y = 0.94;
      body.add(dome);
      const guard = this.meshWithOutline(this.box(0.42, 0.68, 0.12), creamMaterial, 1.035);
      guard.position.set(0, 0.86, -0.34);
      guard.rotation.x = -0.18;
      body.add(guard);
      return body;
    }

    if (kind === 'rapid') {
      const rocket = this.meshWithOutline(this.cylinder(0.24, 0.3, 0.92, 12), shellMaterial, 1.035);
      rocket.rotation.x = Math.PI / 2;
      rocket.position.set(0, 0.82, 0);
      body.add(rocket);
      const nose = new THREE.Mesh(this.cone(0.3, 0.46, 12), glowMaterial);
      nose.rotation.x = -Math.PI / 2;
      nose.position.set(0, 0.82, -0.67);
      body.add(nose);
      for (const x of [-0.24, 0.24]) {
        const fin = new THREE.Mesh(this.shared.box, creamMaterial);
        fin.scale.set(0.1, 0.24, 0.42);
        fin.position.set(x, 0.58, 0.45);
        fin.rotation.z = x > 0 ? -0.3 : 0.3;
        body.add(fin);
      }
      return body;
    }

    if (kind === 'overdrive') {
      const turbine = this.meshWithOutline(this.cylinder(0.52, 0.42, 0.48, 14), shellMaterial, 1.035);
      turbine.rotation.x = Math.PI / 2;
      turbine.position.y = 0.82;
      body.add(turbine);
      const intake = new THREE.Mesh(this.torus(0.38, 0.055, 8, 18), glowMaterial);
      intake.rotation.x = Math.PI / 2;
      intake.position.set(0, 0.82, -0.28);
      body.add(intake);
      for (const x of [-0.38, 0.38]) {
        const booster = new THREE.Mesh(this.cylinder(0.12, 0.16, 0.66, 10), darkMaterial);
        booster.rotation.x = Math.PI / 2;
        booster.position.set(x, 0.72, 0.12);
        body.add(booster);
        const flame = new THREE.Mesh(this.cone(0.13, 0.32, 10), glowMaterial);
        flame.rotation.x = Math.PI / 2;
        flame.position.set(x, 0.72, 0.52);
        body.add(flame);
      }
      return body;
    }

    if (kind === 'mine') {
      const mineBase = this.meshWithOutline(this.cylinder(0.56, 0.66, 0.28, 14), shellMaterial, 1.035);
      mineBase.position.y = 0.32;
      body.add(mineBase);
      const cap = new THREE.Mesh(this.sphere(0.34, 14, 8), glowMaterial);
      cap.scale.y = 0.55;
      cap.position.y = 0.62;
      body.add(cap);
      for (let i = 0; i < 8; i += 1) {
        const angle = i / 8 * Math.PI * 2;
        const spike = new THREE.Mesh(this.shared.tetra, this.materials.hazard);
        spike.position.set(Math.cos(angle) * 0.62, 0.44, Math.sin(angle) * 0.62);
        spike.rotation.set(0.55, angle, 0.35);
        spike.scale.setScalar(0.18);
        body.add(spike);
      }
      return body;
    }

    const droneCore = this.meshWithOutline(this.box(0.62, 0.28, 0.46), shellMaterial, 1.035);
    droneCore.position.y = 0.78;
    body.add(droneCore);
    const sensor = new THREE.Mesh(this.sphere(0.22, 12, 8), glowMaterial);
    sensor.scale.y = 0.65;
    sensor.position.y = 1.03;
    body.add(sensor);
    for (const x of [-0.62, 0.62]) {
      for (const z of [-0.46, 0.46]) {
        const arm = new THREE.Mesh(this.shared.box, darkMaterial);
        arm.scale.set(0.5, 0.06, 0.08);
        arm.position.set(x * 0.48, 0.76, z * 0.48);
        arm.rotation.y = Math.atan2(z, x);
        body.add(arm);
        const rotor = new THREE.Mesh(this.torus(0.19, 0.025, 6, 16), creamMaterial);
        rotor.rotation.x = Math.PI / 2;
        rotor.position.set(x, 0.78, z);
        body.add(rotor);
      }
    }
    return body;
  }

  createPickupPad(level: LevelConfig): any {
    const group = new THREE.Group();
    group.name = 'pickup-pad';
    const ring = new THREE.Mesh(
      new THREE.RingGeometry(0.82, 1.05, 28),
      new THREE.MeshBasicMaterial({
        color: level.palette.accent,
        transparent: true,
        opacity: 0.36,
        side: THREE.DoubleSide,
        depthWrite: false,
        toneMapped: false,
      }),
    );
    ring.rotation.x = -Math.PI / 2;
    ring.position.y = 0.035;
    group.add(ring);

    const ticks = new THREE.InstancedMesh(this.shared.box, this.materials.emissive(level.palette.rim, 0.5), 4);
    const dummy = new THREE.Object3D();
    for (let i = 0; i < 4; i += 1) {
      const angle = i * Math.PI / 2;
      dummy.position.set(Math.cos(angle) * 0.96, 0.08, Math.sin(angle) * 0.96);
      dummy.rotation.set(0, -angle, 0);
      dummy.scale.set(0.38, 0.06, 0.08);
      dummy.updateMatrix();
      ticks.setMatrixAt(i, dummy.matrix);
    }
    ticks.instanceMatrix.needsUpdate = true;
    group.add(ticks);
    return group;
  }

  createHazard(kind: HazardKind, radius: number, level: LevelConfig): any {
    const group = new THREE.Group();
    group.name = `hazard-${kind}`;
    const color = kind === 'spark' ? level.palette.danger : level.scenery === 'desert' ? 0x9b6a42 : 0x3c8f72;
    const fill = new THREE.Mesh(
      new THREE.CircleGeometry(radius, 36),
      new THREE.MeshBasicMaterial({
        color,
        transparent: true,
        opacity: kind === 'spark' ? 0.22 : 0.2,
        side: THREE.DoubleSide,
        depthWrite: false,
        toneMapped: false,
      }),
    );
    fill.rotation.x = -Math.PI / 2;
    fill.position.y = 0.025;
    group.add(fill);

    const outer = new THREE.Mesh(
      new THREE.RingGeometry(radius * 0.88, radius, 36),
      this.materials.emissive(color, kind === 'spark' ? 0.85 : 0.32),
    );
    outer.rotation.x = -Math.PI / 2;
    outer.position.y = 0.04;
    group.add(outer);

    const markerCount = kind === 'spark' ? 8 : 5;
    const markers = new THREE.InstancedMesh(kind === 'spark' ? this.shared.tetra : this.shared.box, this.materials.toon(color), markerCount);
    const dummy = new THREE.Object3D();
    for (let i = 0; i < markerCount; i += 1) {
      const angle = i / markerCount * Math.PI * 2;
      dummy.position.set(Math.cos(angle) * radius * 0.72, 0.08, Math.sin(angle) * radius * 0.72);
      dummy.rotation.set(kind === 'spark' ? 0.35 : 0, angle, kind === 'spark' ? 0.7 : 0);
      const scale = kind === 'spark' ? 0.18 : 0.22;
      dummy.scale.set(scale, scale, kind === 'spark' ? scale : 0.06);
      dummy.updateMatrix();
      markers.setMatrixAt(i, dummy.matrix);
    }
    markers.instanceMatrix.needsUpdate = true;
    group.add(markers);

    if (kind === 'spark') {
      const dangerPlate = new THREE.Mesh(
        new THREE.CylinderGeometry(radius * 0.34, radius * 0.42, 0.08, 18),
        this.materials.darkMetal,
      );
      dangerPlate.position.y = 0.085;
      group.add(dangerPlate);

      const warningBars = new THREE.InstancedMesh(this.shared.box, this.materials.darkMetal, 12);
      for (let i = 0; i < 12; i += 1) {
        const angle = i / 12 * Math.PI * 2;
        dummy.position.set(Math.cos(angle) * radius * 0.92, 0.12, Math.sin(angle) * radius * 0.92);
        dummy.rotation.set(0, -angle, 0);
        dummy.scale.set(0.42, 0.08, 0.09);
        dummy.updateMatrix();
        warningBars.setMatrixAt(i, dummy.matrix);
      }
      warningBars.instanceMatrix.needsUpdate = true;
      group.add(warningBars);

      const teeth = new THREE.InstancedMesh(new THREE.ConeGeometry(0.17, 0.52, 4), this.materials.hazard, 12);
      for (let i = 0; i < 12; i += 1) {
        const angle = (i + 0.5) / 12 * Math.PI * 2;
        dummy.position.set(Math.cos(angle) * radius * 0.74, 0.3, Math.sin(angle) * radius * 0.74);
        dummy.rotation.set(0, angle + Math.PI / 4, 0);
        dummy.scale.setScalar(1);
        dummy.updateMatrix();
        teeth.setMatrixAt(i, dummy.matrix);
      }
      teeth.instanceMatrix.needsUpdate = true;
      group.add(teeth);

      const warning = this.createTextSprite('危', color);
      warning.position.set(0, 0.72, 0);
      warning.scale.set(0.52, 0.52, 0.52);
      group.add(warning);
    }
    return group;
  }

  createMine(color = 0xff5d78): any {
    const root = new THREE.Group();
    root.name = 'gum-mine';
    const shadow = new THREE.Mesh(this.shared.circle24, this.materials.shadow);
    shadow.rotation.x = -Math.PI / 2;
    shadow.scale.set(0.9, 0.9, 1);
    shadow.position.y = 0.025;
    root.add(shadow);

    const body = this.meshWithOutline(this.cylinder(0.42, 0.5, 0.28, 12), this.cachedMaterial(`mineBody:${color.toString(16)}`, () => this.materials.emissive(color, 0.95)), 1.04);
    body.position.y = 0.18;
    root.add(body);

    const spikes = new THREE.InstancedMesh(this.shared.tetra, this.materials.hazard, 6);
    const dummy = new THREE.Object3D();
    for (let i = 0; i < 6; i += 1) {
      const angle = i / 6 * Math.PI * 2;
      dummy.position.set(Math.cos(angle) * 0.5, 0.28, Math.sin(angle) * 0.5);
      dummy.rotation.set(0.55, angle, 0.2);
      dummy.scale.setScalar(0.17);
      dummy.updateMatrix();
      spikes.setMatrixAt(i, dummy.matrix);
    }
    spikes.instanceMatrix.needsUpdate = true;
    root.add(spikes);
    return root;
  }

  createArenaBorder(level: LevelConfig): any {
    const group = new THREE.Group();
    const { width, depth } = level.arena;
    const wallMat = this.materials.toon(level.scenery === 'moon' ? 0x394261 : level.scenery === 'desert' ? 0xa76846 : 0x4f8b65);
    const trimMat = this.materials.emissive(level.palette.rim, 0.35);
    const horizontalSegments = 18;
    const verticalSegments = 14;
    const horizontalCount = horizontalSegments * 2;
    const verticalCount = verticalSegments * 2;
    const horizontalBase = new THREE.InstancedMesh(this.shared.box, wallMat, horizontalCount);
    const horizontalCap = new THREE.InstancedMesh(this.shared.box, trimMat, horizontalCount);
    const verticalBase = new THREE.InstancedMesh(this.shared.box, wallMat, verticalCount);
    const verticalCap = new THREE.InstancedMesh(this.shared.box, trimMat, verticalCount);
    const dummy = new THREE.Object3D();
    let index = 0;
    for (let i = 0; i < horizontalSegments; i += 1) {
      const t = (i + 0.5) / horizontalSegments;
      for (const z of [-depth / 2 - 0.35, depth / 2 + 0.35]) {
        const x = -width / 2 + t * width;
        dummy.position.set(x, 0.34, z);
        dummy.scale.set(width / horizontalSegments - 0.08, 0.65, 0.6);
        dummy.rotation.set(0, 0, 0);
        dummy.updateMatrix();
        horizontalBase.setMatrixAt(index, dummy.matrix);
        dummy.position.y = 0.72;
        dummy.scale.set(width / horizontalSegments - 0.15, 0.12, 0.66);
        dummy.updateMatrix();
        horizontalCap.setMatrixAt(index, dummy.matrix);
        index += 1;
      }
    }
    index = 0;
    for (let i = 0; i < verticalSegments; i += 1) {
      const t = (i + 0.5) / verticalSegments;
      for (const x of [-width / 2 - 0.35, width / 2 + 0.35]) {
        const z = -depth / 2 + t * depth;
        dummy.position.set(x, 0.34, z);
        dummy.scale.set(0.6, 0.65, depth / verticalSegments - 0.08);
        dummy.rotation.set(0, 0, 0);
        dummy.updateMatrix();
        verticalBase.setMatrixAt(index, dummy.matrix);
        dummy.position.y = 0.72;
        dummy.scale.set(0.66, 0.12, depth / verticalSegments - 0.15);
        dummy.updateMatrix();
        verticalCap.setMatrixAt(index, dummy.matrix);
        index += 1;
      }
    }
    for (const mesh of [horizontalBase, horizontalCap, verticalBase, verticalCap]) {
      mesh.instanceMatrix.needsUpdate = true;
      mesh.castShadow = mesh === horizontalBase || mesh === verticalBase;
      mesh.receiveShadow = true;
      group.add(mesh);
    }
    return group;
  }

  createScenery(level: LevelConfig): any {
    const root = new THREE.Group();
    const random = this.seededRandom(1700 + level.id * 67);
    const { width, depth } = level.arena;
    const count = 30;
    const placements: Array<{ x: number; z: number; scale: number; rotation: number; variant: number }> = [];
    for (let i = 0; i < count; i += 1) {
      const side = i % 4;
      let x = 0;
      let z = 0;
      if (side === 0) { x = -width / 2 - 2.2 - random() * 6; z = (random() - 0.5) * (depth + 10); }
      if (side === 1) { x = width / 2 + 2.2 + random() * 6; z = (random() - 0.5) * (depth + 10); }
      if (side === 2) { x = (random() - 0.5) * (width + 8); z = -depth / 2 - 2.3 - random() * 5; }
      if (side === 3) { x = (random() - 0.5) * (width + 8); z = depth / 2 + 2.3 + random() * 5; }
      placements.push({ x, z, scale: 0.7 + random() * 0.8, rotation: random() * Math.PI * 2, variant: random() });
    }

    const dummy = new THREE.Object3D();
    const put = (mesh: any, index: number, x: number, y: number, z: number, sx: number, sy: number, sz: number, rx = 0, ry = 0, rz = 0): void => {
      dummy.position.set(x, y, z);
      dummy.rotation.set(rx, ry, rz);
      dummy.scale.set(sx, sy, sz);
      dummy.updateMatrix();
      mesh.setMatrixAt(index, dummy.matrix);
    };

    if (level.scenery === 'meadow') {
      const trunk = new THREE.InstancedMesh(new THREE.CylinderGeometry(0.18, 0.27, 1.45, 7), this.materials.toon(0x85573b), count);
      const leaves = new THREE.InstancedMesh(new THREE.DodecahedronGeometry(0.76, 0), this.materials.toon(0x63c978), count * 3);
      let leafIndex = 0;
      placements.forEach((placement, index) => {
        const s = placement.scale;
        put(trunk, index, placement.x, 0.73 * s, placement.z, s, s, s, 0, placement.rotation, 0);
        for (let layer = 0; layer < 3; layer += 1) {
          const offsetAngle = placement.rotation + layer * 2.1;
          const offset = 0.18 + layer * 0.06;
          put(leaves, leafIndex++, placement.x + Math.cos(offsetAngle) * offset * s, (1.5 + layer * 0.28) * s, placement.z + Math.sin(offsetAngle) * offset * s, s * (1 - layer * 0.08), s * (0.78 + layer * 0.04), s * (1 - layer * 0.08), 0, placement.rotation + layer, 0);
        }
      });
      trunk.castShadow = true;
      leaves.castShadow = true;
      root.add(trunk, leaves);
    } else if (level.scenery === 'desert') {
      const cactusPlacements = placements.filter((placement) => placement.variant > 0.46);
      const rockPlacements = placements.filter((placement) => placement.variant <= 0.46);
      const cactusTrunks = new THREE.InstancedMesh(new THREE.CylinderGeometry(0.22, 0.28, 1.9, 8), this.materials.toon(0x3c9b72), cactusPlacements.length);
      const cactusArms = new THREE.InstancedMesh(new THREE.CapsuleGeometry(0.14, 0.65, 4, 8), this.materials.toon(0x48aa7d), Math.max(1, cactusPlacements.length * 2));
      const rocks = new THREE.InstancedMesh(new THREE.DodecahedronGeometry(0.9, 0), this.materials.toon(0xb86f4f), Math.max(1, rockPlacements.length * 2));
      let armIndex = 0;
      cactusPlacements.forEach((placement, index) => {
        const s = placement.scale;
        put(cactusTrunks, index, placement.x, 0.95 * s, placement.z, s, s, s, 0, placement.rotation, 0);
        for (const side of [-1, 1]) {
          const localX = side * 0.38 * s;
          const cos = Math.cos(placement.rotation);
          const sin = Math.sin(placement.rotation);
          put(cactusArms, armIndex++, placement.x + localX * cos, 1.05 * s, placement.z - localX * sin, s, s, s, 0, placement.rotation, side * 0.7);
        }
      });
      let rockIndex = 0;
      rockPlacements.forEach((placement) => {
        const s = placement.scale;
        put(rocks, rockIndex++, placement.x, 0.48 * s, placement.z, 1.3 * s, 0.72 * s, s, 0, placement.rotation, 0);
        put(rocks, rockIndex++, placement.x + 0.35 * s, 0.9 * s, placement.z + 0.1 * s, 0.52 * s, 0.52 * s, 0.52 * s, 0, -placement.rotation * 0.7, 0);
      });
      cactusTrunks.castShadow = true;
      cactusArms.castShadow = true;
      rocks.castShadow = true;
      root.add(cactusTrunks, cactusArms, rocks);
    } else {
      const bases = new THREE.InstancedMesh(new THREE.CylinderGeometry(0.48, 0.68, 1.3, 8), this.materials.toon(0x4b526c), count);
      const crystals = new THREE.InstancedMesh(new THREE.ConeGeometry(0.27, 1.35, 5), this.materials.emissive(level.palette.accent, 0.65), count * 3);
      const lights = new THREE.InstancedMesh(new THREE.OctahedronGeometry(0.35, 0), this.materials.emissive(0xffd45c, 0.9), count);
      let crystalIndex = 0;
      placements.forEach((placement, index) => {
        const s = placement.scale;
        put(bases, index, placement.x, 0.65 * s, placement.z, s, s, s, 0, placement.rotation, 0);
        put(lights, index, placement.x, 1.55 * s, placement.z, 0.65 * s, 0.65 * s, 0.65 * s, 0, placement.rotation, 0);
        for (let shard = 0; shard < 3; shard += 1) {
          const angle = placement.rotation + shard * Math.PI * 2 / 3;
          put(crystals, crystalIndex++, placement.x + Math.cos(angle) * 0.62 * s, (0.62 + shard * 0.1) * s, placement.z + Math.sin(angle) * 0.62 * s, (0.65 + shard * 0.12) * s, (0.8 + shard * 0.2) * s, (0.65 + shard * 0.12) * s, (shard - 1) * 0.16, angle, 0);
        }
      });
      bases.castShadow = true;
      crystals.castShadow = true;
      root.add(bases, crystals, lights);
    }

    root.traverse((object: any) => {
      if (object.isInstancedMesh) {
        object.instanceMatrix.needsUpdate = true;
        object.receiveShadow = true;
      }
    });
    return root;
  }

  createSpawnGate(color: number): any {
    const group = new THREE.Group();
    const mat = this.materials.emissive(color, 0.8);
    const dark = this.materials.darkMetal;
    const ring = new THREE.Mesh(new THREE.TorusGeometry(0.9, 0.12, 8, 24), mat);
    ring.rotation.x = Math.PI / 2;
    ring.position.y = 0.08;
    group.add(ring);
    const ticks = new THREE.InstancedMesh(this.shared.box, dark, 4);
    const dummy = new THREE.Object3D();
    for (let i = 0; i < 4; i += 1) {
      dummy.position.set(Math.sin(i * Math.PI / 2) * 1.05, 0.13, Math.cos(i * Math.PI / 2) * 1.05);
      dummy.rotation.set(0, i * Math.PI / 2, 0);
      dummy.scale.set(0.17, 0.16, 0.43);
      dummy.updateMatrix();
      ticks.setMatrixAt(i, dummy.matrix);
    }
    ticks.instanceMatrix.needsUpdate = true;
    group.add(ticks);
    return group;
  }

  private decorateScout(turret: any, mats: Record<string, any>): void {
    for (const side of [-1, 1]) {
      const fin = new THREE.Mesh(this.box(0.08, 0.34, 0.68), mats.signal);
      fin.position.set(side * 0.78, 0.48, 0.28);
      fin.rotation.z = side * -0.22;
      turret.add(fin);
    }
  }

  private decorateBruiser(chassis: any, turret: any, mats: Record<string, any>): void {
    for (const side of [-1, 1]) {
      const armor = this.meshWithOutline(this.box(0.28, 0.7, 1.65), mats.secondary, 1.045);
      armor.position.set(side * 1.28, 0.78, 0.05);
      chassis.add(armor);
      const shoulder = new THREE.Mesh(this.cylinder(0.28, 0.36, 0.5, 8), mats.primary);
      shoulder.position.set(side * 0.74, 0.5, 0.2);
      turret.add(shoulder);
    }
  }

  private decorateSniper(turret: any, mats: Record<string, any>): void {
    const dish = new THREE.Mesh(
      this.cachedGeometry('sphHalf:0.42:12:6', () => new THREE.SphereGeometry(0.42, 12, 6, 0, Math.PI * 2, 0, Math.PI / 2)),
      mats.signal,
    );
    dish.rotation.x = Math.PI;
    dish.position.set(0.48, 0.95, 0.35);
    turret.add(dish);
    const stalk = new THREE.Mesh(this.cylinder(0.04, 0.06, 0.5, 6), mats.dark);
    stalk.position.set(0.48, 0.78, 0.35);
    turret.add(stalk);
  }

  private decorateBoss(chassis: any, turret: any, mats: Record<string, any>): void {
    const crown = new THREE.Group();
    crown.position.set(0, 1.15, 0.25);
    turret.add(crown);
    const band = new THREE.Mesh(this.cylinder(0.5, 0.55, 0.24, 10), mats.trim);
    crown.add(band);
    for (let i = 0; i < 5; i += 1) {
      const spike = new THREE.Mesh(this.cone(0.15, 0.55, 5), i === 2 ? mats.signal : mats.primary);
      const angle = (i / 5 - 0.5) * 1.8;
      spike.position.set(Math.sin(angle) * 0.45, 0.34 + Math.cos(angle) * 0.05, Math.cos(angle) * 0.25);
      crown.add(spike);
    }
    for (const side of [-1, 1]) {
      const cannon = new THREE.Mesh(this.cylinder(0.12, 0.15, 1.3, 10), mats.dark);
      cannon.rotation.x = Math.PI / 2;
      cannon.position.set(side * 0.82, 0.48, -0.85);
      turret.add(cannon);
      const armor = this.meshWithOutline(this.box(0.32, 0.75, 2.0), mats.primary, 1.04);
      armor.position.set(side * 1.36, 0.86, 0.05);
      chassis.add(armor);
    }
  }

  private createTree(random: () => number): any {
    const group = new THREE.Group();
    const trunk = new THREE.Mesh(new THREE.CylinderGeometry(0.18, 0.27, 1.45, 7), this.materials.toon(0x85573b));
    trunk.position.y = 0.73;
    group.add(trunk);
    const leafColors = [0x5ecb75, 0x7fda6a, 0x48b988];
    for (let i = 0; i < 3; i += 1) {
      const leaf = new THREE.Mesh(new THREE.DodecahedronGeometry(0.7 + random() * 0.18, 0), this.materials.toon(leafColors[i]));
      leaf.position.set((random() - 0.5) * 0.55, 1.55 + i * 0.28, (random() - 0.5) * 0.45);
      leaf.scale.y = 0.85;
      group.add(leaf);
    }
    return group;
  }

  private createDesertProp(random: () => number): any {
    const group = new THREE.Group();
    if (random() > 0.48) {
      const cactusMat = this.materials.toon(0x3c9b72);
      const trunk = new THREE.Mesh(new THREE.CylinderGeometry(0.22, 0.28, 1.9, 8), cactusMat);
      trunk.position.y = 0.95;
      group.add(trunk);
      for (const side of [-1, 1]) {
        const arm = new THREE.Mesh(new THREE.CapsuleGeometry(0.14, 0.65, 4, 8), cactusMat);
        arm.position.set(side * 0.36, 1.05 + random() * 0.3, 0);
        arm.rotation.z = side * 0.7;
        group.add(arm);
      }
    } else {
      const rock = new THREE.Mesh(new THREE.DodecahedronGeometry(0.9, 0), this.materials.toon(0xb86f4f));
      rock.scale.set(1.3, 0.72, 1);
      rock.position.y = 0.48;
      group.add(rock);
      const cap = new THREE.Mesh(new THREE.DodecahedronGeometry(0.5, 0), this.materials.toon(0xd58d5c));
      cap.position.set(0.35, 0.9, 0.1);
      group.add(cap);
    }
    return group;
  }

  private createMoonProp(random: () => number, accent: number): any {
    const group = new THREE.Group();
    if (random() > 0.5) {
      const base = new THREE.Mesh(new THREE.CylinderGeometry(0.48, 0.68, 1.3, 8), this.materials.toon(0x4b526c));
      base.position.y = 0.65;
      group.add(base);
      const light = new THREE.Mesh(new THREE.OctahedronGeometry(0.35, 0), this.materials.emissive(accent, 1.2));
      light.position.y = 1.55;
      group.add(light);
      const ring = new THREE.Mesh(new THREE.TorusGeometry(0.46, 0.06, 6, 16), this.materials.toon(0xffcf59));
      ring.rotation.x = Math.PI / 2;
      ring.position.y = 1.48;
      group.add(ring);
    } else {
      const crystalMat = this.materials.emissive(random() > 0.5 ? 0x65e7ff : 0xff6fcc, 0.65);
      for (let i = 0; i < 4; i += 1) {
        const crystal = new THREE.Mesh(new THREE.ConeGeometry(0.22 + random() * 0.18, 1 + random() * 1.3, 5), crystalMat);
        crystal.position.set((random() - 0.5) * 0.8, 0.5 + random() * 0.3, (random() - 0.5) * 0.8);
        crystal.rotation.z = (random() - 0.5) * 0.4;
        group.add(crystal);
      }
    }
    return group;
  }

  /** Get a cached SpriteMaterial or persist the newly built material and texture. */
  private cachedSpriteMaterial(key: string, build: () => any): any {
    const cached = this.spriteMaterialCache.get(key);
    if (cached) return cached;
    const material = build();
    material.userData.persistent = true;
    if (material.map) {
      material.map.userData ??= {};
      material.map.userData.persistent = true;
    }
    this.spriteMaterialCache.set(key, material);
    return material;
  }

  private createTextSprite(text: string, color: number): any {
    const material = this.cachedSpriteMaterial(`icon:${text}:${color}`, () => {
      const canvas = document.createElement('canvas');
      canvas.width = 128;
      canvas.height = 128;
      const context = canvas.getContext('2d')!;
      context.clearRect(0, 0, 128, 128);
      context.fillStyle = '#17283d';
      context.beginPath();
      context.arc(64, 64, 54, 0, Math.PI * 2);
      context.fill();
      context.fillStyle = `#${color.toString(16).padStart(6, '0')}`;
      context.beginPath();
      context.arc(64, 64, 46, 0, Math.PI * 2);
      context.fill();
      context.fillStyle = '#17283d';
      context.font = `900 ${text.length > 1 ? 46 : 58}px sans-serif`;
      context.textAlign = 'center';
      context.textBaseline = 'middle';
      context.fillText(text, 64, 66);
      const texture = new THREE.CanvasTexture(canvas);
      texture.colorSpace = THREE.SRGBColorSpace;
      return new THREE.SpriteMaterial({ map: texture, transparent: true, depthWrite: false });
    });
    return new THREE.Sprite(material);
  }

  private createLabelSprite(text: string, color: number): any {
    const material = this.cachedSpriteMaterial(`label:${text}:${color}`, () => {
      const canvas = document.createElement('canvas');
      canvas.width = 256;
      canvas.height = 96;
      const context = canvas.getContext('2d')!;
      context.clearRect(0, 0, 256, 96);
      context.fillStyle = 'rgba(23,40,61,0.92)';
      this.roundRect(context, 16, 22, 224, 52, 18);
      context.fill();
      context.strokeStyle = `#${color.toString(16).padStart(6, '0')}`;
      context.lineWidth = 5;
      this.roundRect(context, 16, 22, 224, 52, 18);
      context.stroke();
      context.fillStyle = '#fff8df';
      context.font = `900 ${text.length >= 3 ? 34 : 40}px sans-serif`;
      context.textAlign = 'center';
      context.textBaseline = 'middle';
      context.fillText(text, 128, 49);
      const texture = new THREE.CanvasTexture(canvas);
      texture.colorSpace = THREE.SRGBColorSpace;
      return new THREE.SpriteMaterial({ map: texture, transparent: true, depthWrite: false });
    });
    return new THREE.Sprite(material);
  }

  private roundRect(context: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, radius: number): void {
    context.beginPath();
    context.moveTo(x + radius, y);
    context.lineTo(x + width - radius, y);
    context.quadraticCurveTo(x + width, y, x + width, y + radius);
    context.lineTo(x + width, y + height - radius);
    context.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
    context.lineTo(x + radius, y + height);
    context.quadraticCurveTo(x, y + height, x, y + height - radius);
    context.lineTo(x, y + radius);
    context.quadraticCurveTo(x, y, x + radius, y);
    context.closePath();
  }

  private createStarBadge(material: any, outlineMaterial: any): any {
    // Cache the beveled, Earcut-triangulated badge to avoid retriangulating it for every spawned tank.
    const geometry = this.cachedExtrude('star-badge:0.24:0.08:0.05:1', () => {
      const shape = new THREE.Shape();
      const points = 5;
      for (let i = 0; i < points * 2; i += 1) {
        const radius = i % 2 === 0 ? 1 : 0.46;
        const angle = -Math.PI / 2 + i * Math.PI / points;
        const x = Math.cos(angle) * radius;
        const y = Math.sin(angle) * radius;
        if (i === 0) shape.moveTo(x, y); else shape.lineTo(x, y);
      }
      shape.closePath();
      const built = new THREE.ExtrudeGeometry(shape, { depth: 0.24, bevelEnabled: true, bevelSize: 0.08, bevelThickness: 0.05, bevelSegments: 1 });
      built.center();
      return built;
    });
    const group = new THREE.Group();
    const star = new THREE.Mesh(geometry, material);
    star.castShadow = true;
    group.add(star);
    if (outlineMaterial) {
      const outline = new THREE.Mesh(geometry, outlineMaterial);
      outline.scale.setScalar(1.09);
      group.add(outline);
    }
    return group;
  }

  private meshWithOutline(geometry: any, material: any, scale = 1.04): any {
    const group = new THREE.Group();
    const mesh = new THREE.Mesh(geometry, material);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    group.add(mesh);
    const outline = new THREE.Mesh(geometry, this.materials.outline);
    outline.scale.setScalar(scale);
    group.add(outline);
    group.userData.primaryMesh = mesh;
    return group;
  }

  /**
   * Bake geometry and local matrices into one position/normal/index geometry.
   *
   * Tank materials have no UV textures; MeshToonMaterial samples gradientMap from
   * lighting. clone().applyMatrix4() lets Three.js apply the correct normal matrix
   * for nonuniformly scaled parts such as lowerHull and sightBody.
   */
  private mergeParts(parts: Array<{ geometry: any; matrix: any }>): any {
    const baked: any[] = [];
    let vertexCount = 0;
    let indexCount = 0;
    for (const part of parts) {
      const clone = part.geometry.clone();
      clone.applyMatrix4(part.matrix);
      const position = clone.getAttribute('position');
      vertexCount += position.count;
      indexCount += clone.index ? clone.index.count : position.count;
      baked.push(clone);
    }
    const positions = new Float32Array(vertexCount * 3);
    const normals = new Float32Array(vertexCount * 3);
    const indices = vertexCount > 65535 ? new Uint32Array(indexCount) : new Uint16Array(indexCount);
    let vertexOffset = 0;
    let indexOffset = 0;
    for (const clone of baked) {
      const position = clone.getAttribute('position');
      const normal = clone.getAttribute('normal');
      for (let i = 0; i < position.count; i += 1) {
        const target = (vertexOffset + i) * 3;
        positions[target] = position.getX(i);
        positions[target + 1] = position.getY(i);
        positions[target + 2] = position.getZ(i);
        if (normal) {
          normals[target] = normal.getX(i);
          normals[target + 1] = normal.getY(i);
          normals[target + 2] = normal.getZ(i);
        }
      }
      const index = clone.index;
      if (index) {
        for (let i = 0; i < index.count; i += 1) indices[indexOffset + i] = vertexOffset + index.getX(i);
        indexOffset += index.count;
      } else {
        for (let i = 0; i < position.count; i += 1) indices[indexOffset + i] = vertexOffset + i;
        indexOffset += position.count;
      }
      vertexOffset += position.count;
    }
    const merged = new THREE.BufferGeometry();
    merged.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    merged.setAttribute('normal', new THREE.BufferAttribute(normals, 3));
    merged.setIndex(new THREE.BufferAttribute(indices, 1));
    merged.computeBoundingSphere();
    return merged;
  }

  /**
   * Merge static descendants by material to reduce draw calls.
   *
   * A controlled 6x CPU test with 24 scouts reduced per-tank draw calls from
   * 42 to 22 and render p50 from 15.1ms to 9.9ms, with identical triangle counts.
   *
   * Keep independently animated objects in exclude, and preserve InstancedMesh
   * surfaces because they already render in one draw call and cannot be baked here.
   *
   * Cache merged geometry by kind and material UUID; allocating it per tank would
   * reintroduce the geometry leak this optimization removes.
   */
  private collapseStatic(group: any, keyPrefix: string, exclude: Set<any>): void {
    const buckets = new Map<any, Array<{ geometry: any; matrix: any; mesh: any }>>();
    const walk = (node: any, parentMatrix: any): void => {
      for (const child of node.children) {
        if (exclude.has(child)) continue;
        if (child.isInstancedMesh) continue;
        child.updateMatrix();
        const matrix = new THREE.Matrix4().multiplyMatrices(parentMatrix, child.matrix);
        if (child.isMesh && child.geometry && child.material && !Array.isArray(child.material)) {
          let bucket = buckets.get(child.material);
          if (!bucket) {
            bucket = [];
            buckets.set(child.material, bucket);
          }
          bucket.push({ geometry: child.geometry, matrix, mesh: child });
        } else if (child.children.length > 0) {
          walk(child, matrix);
        }
      }
    };
    walk(group, new THREE.Matrix4());

    for (const [material, parts] of buckets) {
      if (parts.length < 2) continue;
      for (const part of parts) part.mesh.parent?.remove(part.mesh);
      const geometry = this.cachedGeometry(`merged:${keyPrefix}:${material.uuid}`, () => this.mergeParts(parts));
      const merged = new THREE.Mesh(geometry, material);
      // Preserve shadow semantics when any member of a merged bucket casts or receives shadows.
      merged.castShadow = parts.some((part) => part.mesh.castShadow);
      merged.receiveShadow = parts.some((part) => part.mesh.receiveShadow);
      group.add(merged);
    }

    // Empty meshWithOutline wrappers still cost updateMatrixWorld traversal, so prune them.
    const prune = (node: any): void => {
      for (const child of [...node.children]) {
        if (exclude.has(child)) continue;
        prune(child);
        if (child.userData?.primaryMesh && child.children.length === 0) node.remove(child);
      }
    };
    prune(group);
  }

  private seededRandom(seed: number): () => number {
    let value = seed >>> 0;
    return () => {
      value = (value * 1664525 + 1013904223) >>> 0;
      return value / 4294967296;
    };
  }
}
